import React from "react";
import { supabase } from "../supabase";
import getSharedAudioContext from "./audioContext";
import {
  cacheSongStemsMapping,
  cacheStemsTracks,
  cacheStemFile,
  getCachedStemsMapping,
  getCachedStemsTracks,
  getCachedStemFile,
  clearCachedStemFiles,
  clearCachedStemsTracks,
  saveStemUserMix,
  getStemUserMix,
  saveStemWaveform,
  getStemWaveform,
} from "../dbSafe";

const STEM_PROXY_BASE = "https://crosstracks-stems-proxy.abryant-drury.workers.dev/api/stems";

const DEFAULT_PACKAGE_ID = "2c75af4f-a139-4052-9b8d-0e8bc2b576bc";

export function useStemPlayer({ songId, isLeader, disabled = false }) {
  const isDisabled = Boolean(disabled);
  const [stemsPackageId, setStemsPackageId] = React.useState(null);
  const [tracks, setTracks] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [isExpanded, setExpanded] = React.useState(false);
  const [isPlaying, setPlaying] = React.useState(false);
  const [accessToken, setAccessToken] = React.useState(null);
  const [userId, setUserId] = React.useState(null);
  const [progress, setProgress] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const progressRef = React.useRef(0);
  const isPlayingRef = React.useRef(false);
  const ctxRef = React.useRef(null);
  const buffersRef = React.useRef(new Map());
  const sourcesRef = React.useRef([]);
  const gainsRef = React.useRef(new Map());
  const pansRef = React.useRef(new Map());
  const startOffsetRef = React.useRef(0);
  const startedAtRef = React.useRef(0);
  const rafRef = React.useRef(null);
  const inFlightRef = React.useRef(null);
  const [waveform, setWaveform] = React.useState([]);

  // keep auth token in sync for proxy fetch
  React.useEffect(() => {
    if (isDisabled) {
      setAccessToken(null);
      setUserId(null);
      return;
    }
    let mounted = true;
    const loadSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (mounted) {
        setAccessToken(data?.session?.access_token || null);
        setUserId(data?.session?.user?.id || null);
      }
    };
    loadSession();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setAccessToken(session?.access_token || null);
        setUserId(session?.user?.id || null);
      }
    });
    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, [isDisabled]);

  React.useEffect(() => {
    if (isDisabled) {
      setStemsPackageId(null);
      setTracks([]);
      setError("Tracks disabled on this device");
      setLoading(false);
      setProgress(0);
      setDuration(0);
      setWaveform([]);
      isPlayingRef.current = false;
      return;
    }
    // reset on song change
    stop();
    setStemsPackageId(null);
    setTracks([]);
    setError("");
    setProgress(0);
    progressRef.current = 0;
    setDuration(0);
    startOffsetRef.current = 0;
    setWaveform([]);
    isPlayingRef.current = false;
  }, [isDisabled, songId]);

  const applyRoleDefaults = (trackList = [], options = {}) => {
    const { isLeaderRole = false } = options;
    return trackList.map((track) => {
      if (!track) return track;
      if (!isLeaderRole) {
        return track.pan_default == null ? { ...track, pan_default: 0 } : track;
      }
      const role = String(track.channel_role || "").toLowerCase();
      const leaderPan = role === "click" || role === "guide" ? -100 : 100;
      return { ...track, pan_default: leaderPan };
    });
  };

  React.useEffect(() => {
    if (isDisabled || !songId) return;
    loadStemsMeta(songId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled, songId]);

  const loadStemsMeta = async (songId, options = {}) => {
    if (isDisabled) return;
    const { setLoadingState = true } = options;
    if (setLoadingState) {
      setLoading(true);
      setError("");
    }
    try {
      const cachedMap = await getCachedStemsMapping(songId);
      if (cachedMap?.stems_package_id) {
        setStemsPackageId(cachedMap.stems_package_id);
        const cachedTracks = await getCachedStemsTracks(cachedMap.stems_package_id);
        if (cachedTracks?.length) setTracks(applyRoleDefaults(cachedTracks, { isLeaderRole: isLeader }));
      }

      // Try fresh fetch
      const { data: mapData, error: mapErr } = await supabase
        .from("songs_stems_map")
        .select("stems_package_id,is_primary")
        .eq("song_id", songId)
        .eq("is_primary", true)
        .maybeSingle();

      const packageId = mapData?.stems_package_id || cachedMap?.stems_package_id || null;

      // Hardcoded fallback for initial test songs
      const hardcoded = songId === 143 || songId === 200 ? DEFAULT_PACKAGE_ID : null;
      const chosenPackage = packageId || hardcoded;

      if (!chosenPackage) {
        setStemsPackageId(null);
        setTracks([]);
        setLoading(false);
        return;
      }

      setStemsPackageId(chosenPackage);
      await cacheSongStemsMapping(songId, { stems_package_id: chosenPackage, is_primary: true });

      const { data: trackData, error: trackErr } = await supabase
        .from("stems_tracks")
        .select("*")
        .eq("stems_package_id", chosenPackage)
        .order("display_order", { ascending: true });

      if (!trackErr && trackData?.length) {
        let trackList = applyRoleDefaults(trackData, { isLeaderRole: isLeader });
        await cacheStemsTracks(chosenPackage, trackData);

        // Load user mix overrides
        if (userId) {
          const overrides = await getStemUserMix(userId, chosenPackage);
          if (overrides?.length) {
            const byTrack = new Map(overrides.map((o) => [o.track_id, o]));
            trackList = trackList.map((t) => {
              const over = byTrack.get(t.id);
              if (!over) return t;
              return {
                ...t,
                volume_default: over.volume ?? t.volume_default,
                pan_default: over.pan ?? t.pan_default,
                muted: over.muted ?? false,
              };
            });
          }
        }
        setTracks(trackList || []);
      } else if (!trackData?.length && !cachedTracks?.length) {
        setTracks([]);
      }
    } catch (err) {
      console.warn("Stems meta error:", err?.message || err);
      if (!stemsPackageId) setError("Stems unavailable offline");
    } finally {
      if (setLoadingState) setLoading(false);
    }
  };

  const runExclusive = async (task) => {
    if (inFlightRef.current) {
      await inFlightRef.current;
    }
    const promise = (async () => {
      setLoading(true);
      try {
        return await task();
      } finally {
        if (inFlightRef.current === promise) inFlightRef.current = null;
        setLoading(false);
      }
    })();
    inFlightRef.current = promise;
    return promise;
  };

  const ensureContext = () => {
    if (isDisabled) return null;
    if (!ctxRef.current) {
      ctxRef.current = getSharedAudioContext();
    }
    return ctxRef.current;
  };

  const loadBuffer = async (pkgId, track, options = {}) => {
    if (isDisabled) return null;
    const { forceDownload = false } = options;
    const ctx = ensureContext();
    if (!ctx) return null;
    if (!forceDownload) {
      const cacheBlob = await getCachedStemFile(pkgId, track.file_name);
      if (cacheBlob) {
        const arrayBuffer = await cacheBlob.arrayBuffer();
        return ctx.decodeAudioData(arrayBuffer.slice(0));
      }
    }

    if (!accessToken) throw new Error("Not authenticated to fetch stems");

    // Fetch from proxy
    const url = `${STEM_PROXY_BASE}/${pkgId}?file=${encodeURIComponent(track.file_name)}`;
    const resp = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!resp.ok) throw new Error(`Failed to fetch stem: ${track.file_name}`);
    const blob = await resp.blob();
    await cacheStemFile(pkgId, track.file_name, blob);
    const buf = await blob.arrayBuffer();
    return ctx.decodeAudioData(buf.slice(0));
  };

  const computeWaveform = (buffers) => {
    if (!buffers.size) return [];
    const sampleCount = 400;
    // Merge by summing absolute values across tracks (mono)
    const firstBuf = buffers.values().next().value;
    const length = Array.from(buffers.values()).reduce((min, buf) => Math.min(min, buf.length), firstBuf.length);
    const step = Math.max(1, Math.floor(length / sampleCount));
    const merged = new Array(sampleCount).fill(0);
    let max = 0;

    for (let i = 0; i < sampleCount; i++) {
      let sum = 0;
      buffers.forEach((buf) => {
        const data = buf.getChannelData(0);
        const start = i * step;
        let acc = 0;
        let count = 0;
        for (let j = start; j < Math.min(start + step, length); j++) {
          acc += Math.abs(data[j]);
          count++;
        }
        sum += count ? acc / count : 0;
      });
      merged[i] = sum;
      if (sum > max) max = sum;
    }

    if (max > 0) {
      for (let i = 0; i < merged.length; i++) merged[i] = merged[i] / max;
    }
    return merged;
  };

  const loadAllBuffers = async (options = {}) => {
    if (isDisabled) return;
    if (!stemsPackageId || !tracks.length) return;
    const buffers = new Map();
    let maxDuration = 0;
    for (const track of tracks) {
      const buf = await loadBuffer(stemsPackageId, track, options);
      buffers.set(track.id, buf);
      if (buf?.duration > maxDuration) maxDuration = buf.duration;
    }
    buffersRef.current = buffers;
    setDuration(maxDuration);

    if (!waveform.length && !options.forceDownload) {
      const cachedWave = await getStemWaveform(stemsPackageId);
      if (cachedWave?.length) {
        setWaveform(cachedWave);
      } else {
        const wf = computeWaveform(buffers);
        if (wf.length) {
          setWaveform(wf);
          await saveStemWaveform(stemsPackageId, wf);
        }
      }
    }
  };

  const preload = async () => {
    if (isDisabled) return;
    if (!tracks.length) return;
    await runExclusive(async () => {
      await loadAllBuffers();
    });
  };

  const play = async (options = {}) => {
    if (isDisabled) return;
    if (!tracks.length) return;
    const { startAt, offset } = options;
    try {
      await runExclusive(async () => {
        await loadAllBuffers();
        const ctx = ensureContext();
        if (Number.isFinite(offset)) {
          startOffsetRef.current = Math.max(0, Number(offset));
        }
        const requestedStart = Number.isFinite(startAt) ? Number(startAt) : null;
        const startTime = requestedStart && requestedStart > ctx.currentTime + 0.01
          ? requestedStart
          : ctx.currentTime + 0.05;
        const localSources = [];

        tracks.forEach((track) => {
          const buffer = buffersRef.current.get(track.id);
          if (!buffer) return;

          const source = ctx.createBufferSource();
          source.buffer = buffer;

          const gain = ctx.createGain();
          const pan = ctx.createStereoPanner();

          const vol = track.muted ? 0 : track.volume_default ?? 1;
          const panVal = ((track.pan_default ?? 0) / 100) || 0;
          gain.gain.value = vol;
          pan.pan.value = panVal;

          source.connect(gain).connect(pan).connect(ctx.destination);

          gainsRef.current.set(track.id, gain);
          pansRef.current.set(track.id, pan);
          source.start(startTime, startOffsetRef.current);
          localSources.push(source);
        });

        sourcesRef.current = localSources;
        isPlayingRef.current = true;
        setPlaying(true);
        startedAtRef.current = startTime - startOffsetRef.current;
        progressRef.current = startOffsetRef.current;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
      });
    } catch (err) {
      console.error("Stem play error:", err);
      setError(err.message || "Playback failed");
    }
  };

  const stop = React.useCallback(
    (offsetOverride = null) => {
      if (isDisabled) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      sourcesRef.current.forEach((src) => {
        try {
          src.stop();
        } catch (_) {
          // ignore
        }
      });
      sourcesRef.current = [];
      if (offsetOverride !== null) {
        startOffsetRef.current = offsetOverride;
      } else if (ctxRef.current && startedAtRef.current) {
        const playedFor = ctxRef.current.currentTime - startedAtRef.current;
        const p = progressRef.current || playedFor;
        const nextOffset = Math.min(p, duration || playedFor);
        startOffsetRef.current = Math.max(0, nextOffset);
      }
      isPlayingRef.current = false;
      setPlaying(false);
      setProgress(startOffsetRef.current);
      progressRef.current = startOffsetRef.current;
    },
    [duration, isDisabled],
  );

  const setTrackVolume = (trackId, value) => {
    if (isDisabled) return;
    const gain = gainsRef.current.get(trackId);
    if (gain) gain.gain.value = getMuteValue(trackId) ? 0 : value;
    setTracks((prev) => prev.map((t) => (t.id === trackId ? { ...t, volume_default: value } : t)));
    if (userId && stemsPackageId) {
      saveStemUserMix(userId, stemsPackageId, trackId, {
        volume: value,
        pan: getPanValue(trackId),
        muted: getMuteValue(trackId),
      });
    }
  };

  const setTrackPan = (trackId, value, options = {}) => {
    if (isDisabled) return;
    const { persist = true } = options;
    const pan = pansRef.current.get(trackId);
    if (pan) pan.pan.value = value;
    setTracks((prev) => prev.map((t) => (t.id === trackId ? { ...t, pan_default: value * 100 } : t)));
    if (persist && userId && stemsPackageId) {
      saveStemUserMix(userId, stemsPackageId, trackId, {
        volume: getVolumeValue(trackId),
        pan: value * 100,
        muted: getMuteValue(trackId),
      });
    }
  };

  const setTrackMute = (trackId, muted) => {
    if (isDisabled) return;
    const gain = gainsRef.current.get(trackId);
    const vol = muted ? 0 : getVolumeValue(trackId);
    if (gain) gain.gain.value = vol;

    setTracks((prev) => prev.map((t) => (t.id === trackId ? { ...t, muted } : t)));
    if (userId && stemsPackageId) {
      saveStemUserMix(userId, stemsPackageId, trackId, {
        volume: getVolumeValue(trackId),
        pan: getPanValue(trackId),
        muted,
      });
    }
  };

  const getVolumeValue = (trackId) => {
    const t = tracks.find((x) => x.id === trackId);
    return t?.volume_default ?? 1;
  };

  const getPanValue = (trackId) => {
    const t = tracks.find((x) => x.id === trackId);
    return t?.pan_default ?? 0;
  };

  const getMuteValue = (trackId) => {
    const t = tracks.find((x) => x.id === trackId);
    return !!t?.muted;
  };

  const seek = async (seconds) => {
    if (isDisabled) return;
    const target = Math.max(0, Math.min(seconds, duration || seconds));
    startOffsetRef.current = target;
    setProgress(target);
    progressRef.current = target;
    if (isPlayingRef.current) {
      stop(target);
      await play();
    }
  };

  const tick = () => {
    if (isDisabled) return;
    if (!isPlayingRef.current || !ctxRef.current) return;
    const pos = ctxRef.current.currentTime - startedAtRef.current;
    const clamped = Math.max(0, Math.min(pos, duration || pos));
    setProgress(clamped);
    progressRef.current = clamped;
    rafRef.current = requestAnimationFrame(tick);
  };

  React.useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const refreshStems = async () => {
    if (isDisabled) return;
    if (!stemsPackageId || isPlayingRef.current) return;
    setError("");
    buffersRef.current = new Map();
    setDuration(0);
    setWaveform([]);
    await runExclusive(async () => {
      await clearCachedStemFiles(stemsPackageId);
      await clearCachedStemsTracks(stemsPackageId);
      if (!tracks.length && songId) {
        await loadStemsMeta(songId, { setLoadingState: false });
      }
      await loadAllBuffers({ forceDownload: true });
    });
  };

  return {
    stemsPackageId,
    tracks,
    loading,
    error,
    isExpanded,
    setExpanded,
    isPlaying,
    refreshStems,
    play,
    stop,
    setTrackVolume,
    setTrackPan,
    setTrackMute,
    seek,
    preload,
    progress,
    duration,
    waveform,
  };
}

export default useStemPlayer;
